import { useCallback, useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { NodeSelection } from '@tiptap/pm/state'
import { Icon } from '../components/Icon'
import { useEscape } from '../useEscape'
import { addBlock, deleteBlock, duplicateBlock, moveBlock, selectBlock } from './block-moves'
import { BLOCKS, type BlockDef } from './blocks'
import { BlockAskForm } from './BlockAskForm'
import { BlockChoices } from './BlockChoices'

/**
 * The gutter beside a block: add one, or take hold of this one.
 *
 * Every editor a writer has used has this, and the reason is not fashion. A
 * block editor without it can only be operated from inside the text — you put
 * the caret somewhere and press something — so the two commonest acts, "put a
 * new block here" and "move this one", have no affordance at all and are
 * discoverable only by being told. The slash menu answers the first from
 * inside the text; nothing answered the second.
 *
 * TWO controls and not one, because they are different jobs. `+` is about the
 * space between blocks; the handle is about the block itself.
 *
 * Positioned rather than decorated. A ProseMirror decoration would put this
 * INSIDE the document, which is the one place it must not be — it is furniture
 * about the content, and a decoration is content as far as every other pass
 * over the document is concerned, including the one that turns this document
 * back into Markdown.
 */
export interface BlockGutterProps {
  editor: Editor | null
  /** The editor's scrolling container, which the gutter is positioned inside. */
  frame: React.RefObject<HTMLDivElement | null>
  readOnly?: boolean
  /**
   * Start a comment about this block. Absent where commenting is not offered.
   *
   * The same shape the toolbar's Comment button sends, because a thread is
   * anchored to the WORDS rather than to a position — this product keeps no
   * block ids in the customer's Markdown — so a comment made from the gutter
   * and one made from a selection have to re-anchor by the same rule.
   */
  onComment?: (selection: { quote: string; prefix: string; suffix: string }) => void
}

/** What the pointer is currently beside. */
interface Hovered {
  pos: number
  top: number
  height: number
}

export function BlockGutter({ editor, frame, readOnly, onComment }: BlockGutterProps) {
  const [at, setAt] = useState<Hovered | null>(null)
  const [menu, setMenu] = useState(false)
  const [picker, setPicker] = useState(false)
  const [asking, setAsking] = useState<BlockDef | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const pickerRef = useRef<HTMLDivElement | null>(null)
  useEscape(menu || picker || asking !== null, closePanels)

  function closePanels() {
    setMenu(false)
    setPicker(false)
    setAsking(null)
    editor?.chain().focus().run()
  }

  useEffect(() => {
    if (!picker) return
    pickerRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
  }, [picker])

  /**
   * Which block the pointer is beside, from the pointer's own Y.
   *
   * `posAtCoords` answers about a point INSIDE the text, and the pointer is in
   * the margin — outside it — where that returns null. So the row is found by
   * asking each top-level child for its box, which is also what makes this
   * work for the blocks that are node views rather than text: a figure, a code
   * group and a tabs strip have no text to be at coordinates of.
   */
  const track = useCallback(
    (clientY: number) => {
      if (!editor || readOnly) return
      const dom = editor.view.dom as HTMLElement
      const box = frame.current?.getBoundingClientRect()
      if (!box) return
      let found: Hovered | null = null
      editor.state.doc.forEach((_node, offset) => {
        if (found) return
        const el = editor.view.nodeDOM(offset) as HTMLElement | null
        if (!el || typeof el.getBoundingClientRect !== 'function') return
        const rect = el.getBoundingClientRect()
        if (clientY >= rect.top - 4 && clientY <= rect.bottom + 4) {
          found = { pos: offset, top: rect.top - box.top, height: rect.height }
        }
      })
      if (!found && dom.childElementCount === 0) return
      setAt(found)
    },
    [editor, frame, readOnly],
  )

  useEffect(() => {
    const el = frame.current
    if (!el || readOnly) return
    const move = (e: PointerEvent) => track(e.clientY)
    // Leaving the frame hides it — a gutter beside nothing is a control
    // pointing at a block the pointer has left.
    const out = (e: PointerEvent) => {
      if (!el.contains(e.relatedTarget as Node)) setAt(null)
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerleave', out)
    return () => {
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerleave', out)
    }
  }, [frame, track, readOnly])

  if (!editor || readOnly || !at) return null

  /** Put the caret in this block first: every command below acts on "here". */
  const here = () => selectBlock(editor, at.pos)

  const act = (run: () => void) => {
    setMenu(false)
    here()
    run()
  }

  const choose = (block: BlockDef) => {
    setPicker(false)
    if (block.ask) {
      setAsking(block)
      return
    }
    block.run(editor, '')
  }

  return (
    <div
      className="blockgutter"
      style={{ top: at.top, height: Math.max(at.height, 22) }}
      data-block-gutter
      // The gutter is furniture: a click here must not put the caret in the
      // text underneath, and a drag from it is the handle's business.
      onMouseDown={(e) => e.preventDefault()}
    >
      <button
        className="blockgutter__btn"
        data-block-add
        aria-label="Add a block below. Hold Alt to add above."
        aria-haspopup="menu"
        aria-expanded={picker || asking !== null}
        aria-controls={picker || asking ? 'block-insert-picker' : undefined}
        data-tip="Add a block below — hold Alt to add above"
        onClick={(e) => {
          here()
          if (!picker && !asking) addBlock(editor, e.altKey ? 'above' : 'below')
          setMenu(false)
          setAsking(null)
          setPicker((open) => !open)
        }}
      >
        <Icon name="plus" size={13} />
      </button>

      <button
        className="blockgutter__btn blockgutter__handle"
        data-block-handle
        aria-label="Move this block, or open its options"
        data-tip="Drag to move · press for options · ⌘⇧↑ and ⌘⇧↓ move it"
        draggable
        onDragStart={(event) => {
          /*
           * Hand the whole NODE to ProseMirror and let its own drag do the
           * rest. Writing a drop of our own would be a second implementation
           * of a thing the editor already does correctly — including the drop
           * cursor, the schema check at the destination, and the undo entry.
           */
          const view = editor.view
          const node = view.state.doc.nodeAt(at.pos)
          if (!node) return
          view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, at.pos)))
          const slice = view.state.selection.content()
          const dom = view.nodeDOM(at.pos) as HTMLElement | null
          if (dom) event.dataTransfer.setDragImage(dom, 8, 8)
          event.dataTransfer.effectAllowed = 'move'
          // ProseMirror reads its own drag off the view; this keeps the
          // browser from treating the gesture as a text drag as well.
          event.dataTransfer.setData('text/html', '')
          view.dragging = { slice, move: true }
        }}
        onDragEnd={() => {
          editor.view.dragging = null
        }}
        onClick={() => {
          here()
          setMenu((open) => !open)
        }}
      >
        <Icon name="blocks" size={13} />
      </button>

      {(picker || asking) && (
        <>
          <div className="popover-scrim" onMouseDown={closePanels} />
          <div
            className="blockgutter__picker"
            ref={pickerRef}
            id="block-insert-picker"
            role={asking ? undefined : 'menu'}
            aria-label={asking ? undefined : 'Choose a block to insert'}
            data-block-picker
            onKeyDown={(event) => {
              if (!picker || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
              const choices = [
                ...(pickerRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ??
                  []),
              ]
              if (choices.length === 0) return
              event.preventDefault()
              const current = Math.max(
                0,
                choices.indexOf(document.activeElement as HTMLButtonElement),
              )
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? choices.length - 1
                    : (current + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) %
                      choices.length
              choices[next]?.focus()
            }}
          >
            {asking?.ask ? (
              <BlockAskForm
                ask={asking.ask}
                className="slash__ask"
                onAnswer={(answer) => {
                  asking.run(editor, answer)
                  closePanels()
                }}
                onCancel={closePanels}
              />
            ) : (
              <BlockChoices items={BLOCKS} onChoose={choose} />
            )}
          </div>
        </>
      )}

      {menu && (
        <>
          <div className="popover-scrim" onMouseDown={() => setMenu(false)} />
          <div className="blockgutter__menu" ref={menuRef} role="menu" data-block-menu>
            {/* No glyph on these two: the icon set has no arrow drawn on the
                24-grid, and the keystroke beside the words is the more useful
                thing to put there anyway — it is how somebody stops needing
                this menu. */}
            <button role="menuitem" onClick={() => act(() => moveBlock(editor, -1))}>
              Move up
              <span className="blockgutter__key">⌘⇧↑</span>
            </button>
            <button role="menuitem" onClick={() => act(() => moveBlock(editor, 1))}>
              Move down
              <span className="blockgutter__key">⌘⇧↓</span>
            </button>
            <button role="menuitem" onClick={() => act(() => duplicateBlock(editor))}>
              <Icon name="pages" size={13} /> Duplicate
            </button>
            {onComment && (
              <button
                role="menuitem"
                data-block-comment
                onClick={() =>
                  act(() => {
                    const { from, to } = editor.state.selection
                    const doc = editor.state.doc
                    onComment({
                      quote: doc.textBetween(from, to, '\n\n'),
                      prefix: doc.textBetween(Math.max(0, from - 32), from, ' '),
                      suffix: doc.textBetween(to, Math.min(doc.content.size, to + 32), ' '),
                    })
                  })
                }
              >
                <Icon name="quote" size={13} /> Comment on this block
              </button>
            )}
            <button
              role="menuitem"
              className="blockgutter__danger"
              onClick={() => act(() => deleteBlock(editor))}
            >
              <Icon name="trash" size={13} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}
